import { InjectionToken } from '@angular/core';

export interface SupportCredentials {
  initialContextMessage?: string;
  contextMessageIds?: string[];
  contactId: string;
  participantId: string;
  participantToken: string;
  region: string;
}
export interface SupportItem {
  Id?: string;
  Type?: string;
  Content?: string;
  ContentType?: string;
  ParticipantRole?: string;
  DisplayName?: string;
  ParticipantId?: string;
  AbsoluteTime?: string;
}
export interface SupportSession {
  connect(): Promise<unknown>;
  disconnectParticipant(): Promise<unknown>;
  sendMessage(value: { contentType: string; message: string }): Promise<{ data: SupportItem }>;
  getTranscript(value: {
    maxResults: number;
    sortOrder: string;
    nextToken?: string;
  }): Promise<{ data: { Transcript?: SupportItem[]; NextToken?: string } }>;
  onMessage(handler: (event: { data: SupportItem }) => void): void;
  onEnded(handler: () => void): void;
  onConnectionLost(handler: () => void): void;
  onConnectionEstablished(handler: () => void): void;
  onConnectionBroken(handler: () => void): void;
}
export type SupportSessionFactory = (credentials: SupportCredentials) => SupportSession;

/** Lazy load only for handoff; no CDN scripts, browser storage, or SDK message logging. */
export const SUPPORT_SESSION_FACTORY = new InjectionToken<() => Promise<SupportSessionFactory>>(
  'Connect chat session factory',
  {
    providedIn: 'root',
    factory: () => async () => {
      await import('amazon-connect-chatjs/dist/amazon-connect-chat.js');
      const sdk = (
        window as unknown as {
          connect: {
            ChatSession: {
              setGlobalConfig(config: unknown): void;
              create(config: unknown): SupportSession;
            };
          };
        }
      ).connect.ChatSession;
      const silent = () => undefined;
      sdk.setGlobalConfig({
        loggerConfig: {
          useDefaultLogger: false,
          customizedLogger: {
            debug: silent,
            info: silent,
            warn: silent,
            error: silent,
            advancedLog: silent,
          },
        },
        features: { messageReceipts: { shouldSendMessageReceipts: false } },
      });
      return ({ region, ...chatDetails }) =>
        sdk.create({
          chatDetails,
          options: { region },
          type: 'CUSTOMER',
          disableCSM: true,
        });
    },
  },
);
